import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataStore, useUIStore } from '../stores';
import dataService from '../services/DataService';
import './ProjectDetail.css';

function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const projects = useDataStore(state => state.projects);
  const showSuccessNotification = useUIStore(state => state.showSuccessNotification);
  const showErrorNotification = useUIStore(state => state.showErrorNotification);

  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('updates'); // 'updates' or 'rooms'
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showImageForm, setShowImageForm] = useState({});
  const [formData, setFormData] = useState({
    update_text: '',
    room_name: '',
    files: []
  });
  const [roomFormData, setRoomFormData] = useState({
    room_name: '',
    description: ''
  });
  const [imageFormData, setImageFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  const loadProjectData = async () => {
    setIsLoading(true);
    try {
      // Load project from store or API
      const projectFromStore = projects.find(p => p.id === parseInt(projectId));
      if (projectFromStore) {
        setProject(projectFromStore);
      } else {
        // Fetch from API if not in store
        const projectData = await dataService.getProjectById(parseInt(projectId));
        setProject(projectData);
      }

      // Load project updates and rooms
      await Promise.all([
        loadProjectUpdates(),
        loadProjectRooms()
      ]);
    } catch (error) {
      console.error('Error loading project:', error);
      showErrorNotification('Failed to load project details');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProjectUpdates = async () => {
    try {
      const updatesData = await dataService.getProjectUpdates(parseInt(projectId));
      setUpdates(Array.isArray(updatesData) ? updatesData : []);
    } catch (error) {
      console.error('Error loading project updates:', error);
      // If endpoint doesn't exist yet, set empty array
      setUpdates([]);
    }
  };

  const loadProjectRooms = async () => {
    try {
      const roomsData = await dataService.getProjectRooms(parseInt(projectId));
      setRooms(Array.isArray(roomsData) ? roomsData : []);
    } catch (error) {
      console.error('Error loading project rooms:', error);
      // If endpoint doesn't exist yet, set empty array
      setRooms([]);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.update_text.trim()) {
      newErrors.update_text = 'Update text is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        update_text: formData.update_text.trim(),
        ...(formData.room_name.trim() && { room_name: formData.room_name.trim() })
      };

      await dataService.createProjectUpdate(parseInt(projectId), updateData, formData.files);
      
      showSuccessNotification('Project update created successfully');
      setFormData({ update_text: '', room_name: '', files: [] });
      setShowUpdateForm(false);
      await loadProjectUpdates();
    } catch (error) {
      console.error('Error creating project update:', error);
      let errorMessage = 'Failed to create project update';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoomFormChange = (e) => {
    const { name, value } = e.target;
    setRoomFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageFormChange = (roomId, e) => {
    const { name, value, files } = e.target;
    setImageFormData(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [name]: files ? files[0] : value
      }
    }));
  };

  const validateRoomForm = () => {
    const newErrors = {};
    
    if (!roomFormData.room_name.trim()) {
      newErrors.room_name = 'Room name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateRoomForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dataService.createProjectRoom(parseInt(projectId), {
        room_name: roomFormData.room_name.trim(),
        description: roomFormData.description.trim()
      });
      
      showSuccessNotification('Room created successfully');
      setRoomFormData({ room_name: '', description: '' });
      setShowRoomForm(false);
      await loadProjectRooms();
    } catch (error) {
      console.error('Error creating room:', error);
      let errorMessage = 'Failed to create room';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSubmit = async (roomId, e) => {
    e.preventDefault();
    
    const imageData = imageFormData[roomId];
    if (!imageData || !imageData.file) {
      showErrorNotification('Please select an image file');
      return;
    }

    setIsSubmitting(true);

    try {
      await dataService.addRoomTimelineImage(
        parseInt(projectId),
        roomId,
        imageData.file,
        imageData.description || ''
      );
      
      showSuccessNotification('Image added to room timeline successfully');
      setImageFormData(prev => ({
        ...prev,
        [roomId]: { file: null, description: '' }
      }));
      setShowImageForm(prev => ({
        ...prev,
        [roomId]: false
      }));
      await loadProjectRooms();
    } catch (error) {
      console.error('Error adding timeline image:', error);
      let errorMessage = 'Failed to add image';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group updates by room
  const groupedUpdates = updates.reduce((acc, update) => {
    const roomName = update.room_name || 'General';
    if (!acc[roomName]) {
      acc[roomName] = [];
    }
    acc[roomName].push(update);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  if (isLoading) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-loading">
          <p>Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-error">
          <p>Project not found</p>
          <button onClick={() => navigate('/admin')} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-container">
      <div className="project-detail-header">
        <button onClick={() => navigate('/admin')} className="back-button">
          ← Back to Dashboard
        </button>
        <div className="project-detail-title">
          <h1>{project.project_title}</h1>
          <span className={`status-badge ${project.status}`}>{project.status}</span>
        </div>
      </div>

      <div className="project-detail-content">
        <div className="project-info-section">
          <h2>Project Information</h2>
          <div className="project-info-grid">
            <div className="project-info-item">
              <label>Client Name</label>
              <p>{project.client_name || 'N/A'}</p>
            </div>
            <div className="project-info-item">
              <label>Address</label>
              <p>{project.address || 'N/A'}</p>
            </div>
            <div className="project-info-item">
              <label>Start Date</label>
              <p>{formatDate(project.start_date)}</p>
            </div>
            <div className="project-info-item">
              <label>Expected Completion</label>
              <p>{formatDate(project.expected_completion_date)}</p>
            </div>
            {project.completion_date && (
              <div className="project-info-item">
                <label>Completion Date</label>
                <p>{formatDate(project.completion_date)}</p>
              </div>
            )}
            <div className="project-info-item">
              <label>Budget</label>
              <p>₹{project.budget?.toLocaleString('en-IN') || 'N/A'}</p>
            </div>
            <div className="project-info-item">
              <label>Progress</label>
              <div className="progress-display">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${project.progress || 0}%` }}
                  ></div>
                </div>
                <span>{project.progress || 0}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="project-updates-section">
          <div className="section-tabs">
            <button
              className={activeTab === 'updates' ? 'active' : ''}
              onClick={() => setActiveTab('updates')}
            >
              📝 Updates
            </button>
            <button
              className={activeTab === 'rooms' ? 'active' : ''}
              onClick={() => setActiveTab('rooms')}
            >
              🏠 Rooms
            </button>
          </div>

          {activeTab === 'updates' && (
            <>
              <div className="updates-header">
                <h2>Project Updates</h2>
                <button 
                  onClick={() => setShowUpdateForm(!showUpdateForm)}
                  className="btn-primary"
                >
                  {showUpdateForm ? 'Cancel' : '+ Add Update'}
                </button>
              </div>

          {showUpdateForm && (
            <form onSubmit={handleSubmit} className="update-form">
              <div className="form-group">
                <label htmlFor="update_text">Update Text *</label>
                <textarea
                  id="update_text"
                  name="update_text"
                  value={formData.update_text}
                  onChange={handleChange}
                  className={errors.update_text ? 'error' : ''}
                  rows="4"
                  placeholder="Describe the project update..."
                  disabled={isSubmitting}
                />
                {errors.update_text && <span className="error-text">{errors.update_text}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="room_name">Room Name (Optional)</label>
                <input
                  type="text"
                  id="room_name"
                  name="room_name"
                  value={formData.room_name}
                  onChange={handleChange}
                  placeholder="e.g., Kitchen, Living Room, Room 1"
                  disabled={isSubmitting}
                />
                <small className="form-hint">Leave empty for general updates. Room will be created automatically if it doesn't exist.</small>
              </div>

              <div className="form-group">
                <label htmlFor="files">Attach Files (Optional)</label>
                <input
                  type="file"
                  id="files"
                  name="files"
                  onChange={handleFileChange}
                  multiple
                  accept="image/*,video/*"
                  disabled={isSubmitting}
                />
                {formData.files.length > 0 && (
                  <div className="file-list">
                    {formData.files.map((file, index) => (
                      <div key={index} className="file-item">
                        <span>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="remove-file-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Update'}
                </button>
              </div>
            </form>
          )}

          {Object.keys(groupedUpdates).length === 0 ? (
            <div className="no-updates">
              <p>No updates yet. Create the first update to get started!</p>
            </div>
          ) : (
            <div className="updates-list">
              {Object.entries(groupedUpdates).map(([roomName, roomUpdates]) => (
                <div key={roomName} className="room-updates-group">
                  <h3 className="room-name">{roomName}</h3>
                  {roomUpdates.map(update => (
                    <div key={update.id} className="update-item">
                      <div className="update-header">
                        <span className="update-date">{formatDate(update.created_at)}</span>
                      </div>
                      <div className="update-content">
                        <p>{update.update_text}</p>
                        {update.files && update.files.length > 0 && (
                          <div className="update-files">
                            {update.files.map((file, index) => (
                              <div key={index} className="update-file">
                                {file.type?.startsWith('image/') ? (
                                  <img src={file.url || file} alt={`Update ${index + 1}`} />
                                ) : (
                                  <a href={file.url || file} target="_blank" rel="noopener noreferrer">
                                    📎 {file.name || `File ${index + 1}`}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
            </>
          )}

          {activeTab === 'rooms' && (
            <>
              <div className="updates-header">
                <h2>Project Rooms</h2>
                <button 
                  onClick={() => setShowRoomForm(!showRoomForm)}
                  className="btn-primary"
                >
                  {showRoomForm ? 'Cancel' : '+ Create Room'}
                </button>
              </div>

              {showRoomForm && (
                <form onSubmit={handleRoomSubmit} className="update-form">
                  <div className="form-group">
                    <label htmlFor="room_name">Room Name *</label>
                    <input
                      type="text"
                      id="room_name"
                      name="room_name"
                      value={roomFormData.room_name}
                      onChange={handleRoomFormChange}
                      className={errors.room_name ? 'error' : ''}
                      placeholder="e.g., Kitchen, Living Room, Bedroom"
                      disabled={isSubmitting}
                    />
                    {errors.room_name && <span className="error-text">{errors.room_name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="room_description">Description (Optional)</label>
                    <textarea
                      id="room_description"
                      name="description"
                      value={roomFormData.description}
                      onChange={handleRoomFormChange}
                      rows="3"
                      placeholder="Add a description for this room..."
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Room'}
                    </button>
                  </div>
                </form>
              )}

              {rooms.length === 0 ? (
                <div className="no-updates">
                  <p>No rooms created yet. Create the first room to get started!</p>
                </div>
              ) : (
                <div className="rooms-list">
                  {rooms.map(room => (
                    <div 
                      key={room.id} 
                      className="room-card clickable"
                      onClick={() => navigate(`/admin/projects/${projectId}/rooms/${room.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="room-card-header">
                        <div>
                          <h3>{room.room_name}</h3>
                          {room.description && (
                            <p className="room-description">{room.description}</p>
                          )}
                          <p className="room-meta">
                            {room.timeline_images_count || 0} timeline images
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowImageForm(prev => ({
                              ...prev,
                              [room.id]: !prev[room.id]
                            }));
                          }}
                          className="btn-secondary"
                        >
                          {showImageForm[room.id] ? 'Cancel' : '+ Add Image'}
                        </button>
                      </div>

                      {showImageForm[room.id] && (
                        <form 
                          onSubmit={(e) => handleImageSubmit(room.id, e)} 
                          className="image-form"
                        >
                          <div className="form-group">
                            <label htmlFor={`image_${room.id}`}>Select Image *</label>
                            <input
                              type="file"
                              id={`image_${room.id}`}
                              name="file"
                              accept="image/*"
                              onChange={(e) => handleImageFormChange(room.id, e)}
                              disabled={isSubmitting}
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor={`image_desc_${room.id}`}>Description (Optional)</label>
                            <input
                              type="text"
                              id={`image_desc_${room.id}`}
                              name="description"
                              value={imageFormData[room.id]?.description || ''}
                              onChange={(e) => handleImageFormChange(room.id, e)}
                              placeholder="Add a description for this image..."
                              disabled={isSubmitting}
                            />
                          </div>

                          <div className="form-actions">
                            <button 
                              type="submit" 
                              className="btn-primary"
                              disabled={isSubmitting || !imageFormData[room.id]?.file}
                            >
                              {isSubmitting ? 'Uploading...' : 'Add Image'}
                            </button>
                          </div>
                        </form>
                      )}

                      {/* Room timeline images */}
                      {(room.timeline_images_count > 0 || (room.timeline_images && room.timeline_images.length > 0)) && (
                        <div className="room-timeline">
                          <h4>Timeline Images ({room.timeline_images_count || room.timeline_images?.length || 0})</h4>
                          {room.timeline_images && room.timeline_images.length > 0 ? (
                            <div className="timeline-images-grid">
                              {room.timeline_images.map((image, index) => (
                                <div key={image.id || index} className="timeline-image-item">
                                  <img 
                                    src={image.file_path || image.url || image} 
                                    alt={image.description || `Timeline image ${index + 1}`}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<div style="padding: 2rem; text-align: center; color: #999;">Image not available</div>`;
                                    }}
                                  />
                                  {image.description && (
                                    <p className="image-description">{image.description}</p>
                                  )}
                                  <span className="image-date">{formatDate(image.created_at)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="no-images-message">Images will appear here once uploaded.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectDetail;

